<template>
  <div v-if="isVisible">
    <KikButton
      class="w-full !bg-paypal-300 !text-[#001c64] hover:!bg-paypal-400 active:!bg-paypal-400"
      variant="primary"
      data-testid="btn__paypal-checkout"
      :uppercase="true"
      :disabled="loading"
      @click="startCheckout"
    >
      <SfLoaderCircular v-if="loading" />
      <template v-else>
        {{ $t('Checkout with') }}
        <NuxtImg
          class="w-20"
          src="/img/icons/payments/paypal-button.svg"
          :alt="$t('PayPal')"
        />
      </template>
    </KikButton>
    <p class="border border-paypal-300 border-t-transparent bg-paypal-50 px-2 py-0.5 text-center text-xs text-paypal-700">
      {{ $t('Please check your delivery address before completing your purchase') }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { SfLoaderCircular } from '@storefront-ui/vue';
import { useCart } from '~/composables/custom/useCart/useCart';
import useUiNotification from '~/composables/useUiNotification';
import { usePayment } from '~/composables/custom/usePayment/usePayment';
import { useTracking } from '~/composables/custom/useTracking';

interface Props {
  color?: 'grey' | 'yellow';
}

withDefaults(defineProps<Props>(), {
  color: 'grey',
});

const { $flags, $domain } = useNuxtApp();
const { cart } = useCart();
const {
  redirectUrl, createPaymentOrder, errors, loading,
} = usePayment();
const route = useRoute();
const { send } = useUiNotification();
const { trackStartCheckout } = useTracking();

const startCheckout = async () => {
  await createPaymentOrder(undefined, { isPaypalExpress: true });
  if (errors.value.createPaymentOrder || !redirectUrl.value) {
    if (cart.value?.lineItems?.length === 0) {
      await navigateTo('/');
    }
    return;
  }
  trackStartCheckout();
  window.location.href = redirectUrl.value;
};

const isVisible = computed(() => {
  // Temporary check for PayPal Express in Poland
  const isPaypalExpressEnabled = $domain.countryCode === 'PL' ? $flags.has('payment.paypalecom.paypal_express') : $flags.has('payment.paypal.paypal_express');
  const isClickAndCollect = cart.value?.shippingInfo?.shippingMethod?.custom?.customFieldsRaw?.some(customField => customField.name === 'type' && customField.value === 'shop') ?? false;

  return isPaypalExpressEnabled && !isClickAndCollect;
});

onMounted(() => {
  const { errorCode } = route.query || {};

  if (errorCode) {
    const errorCodes = (errorCode as string)?.split(',');

    if (errorCodes?.length > 0) {
      errorCodes.forEach(code => (
        send({
          message: code === 'paymentCanceled' ? 'paymentAborted' : code,
          type: 'danger',
        })
      ));
    }
  }
});
</script>
